import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Card, CardContent, MenuItem } from "@mui/material";
import { useCphDataContext } from "../../hooks/useCphDataContext";
import { useCphContext } from "../../contexts/CphContext";
import { useModifyStaffTitle, useSetIssuePriority } from "../../services/ApiHandlerService";

const TitleCard: React.FC = () => {
    const { selectedIssue } = useCphDataContext();
    const { isStaff, prioritiesList } = useCphContext();
    const { modifyStaffTitle, loading, error, success } = useModifyStaffTitle();
    const { setIssuePriority } = useSetIssuePriority(); 
    const [modalOpen, setModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState(selectedIssue?.staffTitle || '');

    // Ensure inputValue is always initialized to selectedIssue.staffTitle when modal opens
    useEffect(() => {
        if (modalOpen) {
            setInputValue(selectedIssue?.staffTitle || '');
        }
    }, [modalOpen, selectedIssue]);

    const handleSubmit = async () => {
        await modifyStaffTitle(selectedIssue!.issueKey!, inputValue);
        setModalOpen(false);
        setInputValue(selectedIssue?.staffTitle || '');
    };

    const handlePriorityChange = async (priorityKey: string) => {
        await setIssuePriority(selectedIssue!.issueKey!, priorityKey);
    };

    if (!selectedIssue) {
        return <Typography variant="body1">No issue selected.</Typography>;
    }

    const selectedPriorityValue =
        selectedIssue.priority
            ? ((prioritiesList ?? []).find(
                item => item.Code?.toLowerCase() === selectedIssue.priority?.toLowerCase()
            )?.Key ?? "")
            : "";

    return (
        <Card sx={{ width: '100%', height: '100%', maxWidth: '100%', mx: 0, px: 0, py: 0 }}>
            <CardContent>
                {isStaff ? (
                    <>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', width: '100%' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <Typography variant="body2">
                                    <strong>Issue:</strong> {selectedIssue.title}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Staff Note:</strong> {selectedIssue.staffTitle}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Assigned To:</strong>
                                    {Array.isArray(selectedIssue.assignedTo) && selectedIssue.assignedTo.length > 0
                                        ? selectedIssue.assignedTo
                                            .map(person => ` ${person.firstName}${person.lastName ? ' ' + person.lastName : ''}`)
                                            .join(', ')
                                        : ""}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2" sx={{ mr: 1, minWidth: 60 }}>
                                        <strong>Priority:</strong>
                                    </Typography>
                                    <TextField
                                        id="priority_textbox"
                                        variant="outlined"
                                        name="priority"
                                        size="small"
                                        select
                                        value={selectedPriorityValue}
                                        onChange={(event) => handlePriorityChange(event.target.value)}
                                        SelectProps={{
                                            displayEmpty: true,
                                            renderValue: (selected) => {
                                                if (!selected) {
                                                    return 'Select Priority';
                                                }
                                                const selectedKey = String(selected);
                                                const selectedPriority = (prioritiesList ?? []).find(
                                                    item => item.Key === selectedKey
                                                );
                                                return selectedPriority?.Code ?? selectedKey;
                                            },
                                        }}
                                        sx={{ width: '130px', '& .MuiInputBase-input': { padding: '6px 8px' } }}
                                        InputLabelProps={{ sx: { fontSize: 12 } }}
                                        inputProps={{ style: { height: '1.2em', padding: '6px 8px' } }}
                                    >
                                        {(prioritiesList ?? []).map((priorityItem) => (
                                            <MenuItem
                                                key={priorityItem.Key ?? `${priorityItem.Code ?? ''}-${priorityItem.Description ?? ''}`}
                                                value={priorityItem.Key ?? ''}
                                            >
                                                {priorityItem.Code ?? priorityItem.Key ?? 'Unknown Priority'}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', marginLeft: 16 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ mt: 1, ml: 1 }}
                                    onClick={() => setModalOpen(true)}
                                    disabled={modalOpen}
                                >Modify Staff Note</Button>
                            </div>
                        </div>
                        {modalOpen ? (
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <br />
                                <TextField
                                    label="Staff Note"
                                    value={inputValue}
                                    onChange={e => setInputValue(e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button variant="contained" color="primary" onClick={handleSubmit}>Submit</Button>
                                    <Button variant="outlined" color="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                                </Box>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Typography variant="body2">
                            <strong>Issue:</strong> {selectedIssue.title}
                        </Typography>
                    </div>
                )}
            </CardContent>
        </Card >

    );
};

export default TitleCard;